/**
 * The plain-language phase a running chat turn is in, derived from the stream.
 *
 * The backend emits a verbose step per pipeline node ("Intent Classification", "SQL Agent —
 * Ran query", …). Read one at a time those are a developer's log; what a reader wants is a
 * handful of stages and how far along the turn is. Each stream event maps to a phase, and the
 * phase only moves forward: an unknown or legacy step (the backend infers `intent_classified`
 * for rows stored without a type) must never drag the label back to "Understanding".
 *
 * The one deliberate regression is `continuation_fallback`: the turn genuinely restarts from
 * the question, and the label should say so.
 */

export const TURN_PHASES = [
  "understanding",
  "planning",
  "continuing",
  "querying",
  "writing",
  "explaining",
  "finishing",
] as const;

export type TurnPhase = (typeof TURN_PHASES)[number];

export type PhaseSource = {
  eventType: string;
  stepTitle?: string;
  /** `answer_part` only. */
  part?: string;
  /** `answer_part` insights only: why and action are still being written. */
  narrativePending?: boolean;
};

const PHASE_BY_EVENT: Record<string, TurnPhase> = {
  intent_classified: "understanding",
  preference_context_applied: "understanding",
  concept_resolved: "understanding",
  rag_retrieval: "understanding",
  graph_search: "understanding",
  document_router: "understanding",
  clarification_required: "understanding",
  clarification_answered: "understanding",
  waiting_for_clarification: "understanding",
  planning_decision: "planning",
  analysis_plan: "planning",
  continuation_planned: "continuing",
  continuation_fallback: "planning",
  query_cost_estimated: "querying",
  sql_generation_attempt: "querying",
  sql_execution_result: "writing",
  result_summary: "writing",
  synthesis_completed: "writing",
  critique_completed: "writing",
  document_retrieval: "writing",
  document_qa: "writing",
  document_reconciled: "writing",
  answer_validated: "writing",
  // The parallel answer review (writer, evidence gate and evidence review side by side).
  answer_review_started: "writing",
  answer_review_completed: "writing",
  answer_checked: "writing",
  why_research: "explaining",
  token: "finishing",
  turn_completed: "finishing",
};

/** `tool_call` / `tool_result` steps carry only a title; its prefix names the node. */
const PHASE_BY_TITLE_PREFIX: Array<[string, TurnPhase]> = [
  ["Context Planning", "planning"],
  ["SQL Agent", "querying"],
  ["DDL Q&A", "writing"],
  ["Answer Validation", "writing"],
  ["Response Formatting", "finishing"],
];

function mappedPhase(source: PhaseSource): TurnPhase | null {
  if (source.eventType === "answer_part") {
    if (source.part === "insights") {
      return source.narrativePending ? "explaining" : "finishing";
    }
    return source.part ? "writing" : null;
  }
  const byEvent = PHASE_BY_EVENT[source.eventType];
  if (byEvent) {
    return byEvent;
  }
  const title = source.stepTitle ?? "";
  const byTitle = PHASE_BY_TITLE_PREFIX.find(([prefix]) => title.startsWith(prefix));
  return byTitle ? byTitle[1] : null;
}

export function phaseIndex(phase: TurnPhase): number {
  return TURN_PHASES.indexOf(phase);
}

/** The phase after `source`, never earlier than `current` except on a continuation fallback. */
export function derivePhase(current: TurnPhase | null, source: PhaseSource): TurnPhase | null {
  const next = mappedPhase(source);
  if (next === null) {
    return current;
  }
  if (source.eventType === "continuation_fallback") {
    return next;
  }
  if (current === null || phaseIndex(next) > phaseIndex(current)) {
    return next;
  }
  return current;
}

/** Progress for a determinate bar: the share of phases reached, 0–100. */
export function phaseProgress(phase: TurnPhase | null): number {
  if (phase === null) {
    return 0;
  }
  return Math.round(((phaseIndex(phase) + 1) / TURN_PHASES.length) * 100);
}

/** `12s`, `1m 05s`: short enough to sit beside the phase label. */
export function formatElapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  if (whole < 60) {
    return `${whole}s`;
  }
  const minutes = Math.floor(whole / 60);
  const remainder = whole % 60;
  return `${minutes}m ${remainder.toString().padStart(2, "0")}s`;
}
